import React, {Component} from "react";
import "./PageUpload.css";
import {MainContent, MainHeader} from "../components/Layout";
import {getSearchParams} from "../utils/getSearchParams";
import {formatFileSize, requestApiJson} from "../utils/Utils";

export class PageUpload extends Component {

    PAGE_STATUS = {
        NOT_STARTED: "NOT_STARTED",
        UPLOADING: "UPLOADING",
        INDEXING: "INDEXING",
        DONE: "DONE"
    };

    FILE_STATUS = {
        WAITING_FOR_UPLOAD: "WAITING_FOR_UPLOAD",
        UPLOADING: "UPLOADING",
        PROCESSING: "PROCESSING",
        DONE: "DONE",
        ERROR: "ERROR"
    };

    POLLING_PERIOD = 2000; // ms

    constructor(props) {
        super(props);

        let params = getSearchParams();
        let path = params.path ? decodeURIComponent(params.path) : "/";

        this.state = {
            path: path,
            files: [],
            status: this.PAGE_STATUS.NOT_STARTED
        };
    }

    getResourceLink = (id) => {
        return "/view/" + id;
    };

    upload = (e) => {
        e.preventDefault();

        if (!this.state.files) {
            return;
        }

        this.setState({
            status: this.PAGE_STATUS.UPLOADING
        }, () => {
            this.uploadFiles();
        });
    };

    updateFile = (i, modifier) => {
        let files = this.state.files;
        modifier(files[i]);
        this.setState({files: [...files]});
    };

    uploadFiles = async () => {
        let files = this.state.files;
        this.state.files.forEach(file => file.status = this.FILE_STATUS.WAITING_FOR_UPLOAD);
        this.setState({files: [...files]});

        for (let i = 0; i < this.state.files.length; i++) {
            const file = this.state.files[i];

            // update state
            this.updateFile(i, file => file.status = this.FILE_STATUS.UPLOADING);

            // get upload link
            const request = {
                folder: this.state.path,
                fileName: file.name,
                timeCreated: null,
                timeModified:  new Date(file.lastModified).toISOString()
            };

            let response = await requestApiJson("/uploads", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify(request)
            });

            this.updateFile(i, file => file.id = response.id);

            // upload using provided link
            /*let uploadResponse =*/ await fetch(response.url, {
                method: "PUT",
                body: file
            });

            // update state
            this.updateFile(i, file => file.status = this.FILE_STATUS.PROCESSING);
        }

        this.setState({status: this.PAGE_STATUS.INDEXING});
        setTimeout(this.pollStatus, this.POLLING_PERIOD);
    };

    pollStatus = async () => {
        let hasPendingFiles = false;

        for (let i = 0; i < this.state.files.length; i++) {
            const file = this.state.files[i];
            if (file.status === this.FILE_STATUS.PROCESSING) {
                let response = await requestApiJson("/uploads/" + file.id, {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json"
                    }
                });

                if (response.status !== this.FILE_STATUS.WAITING_FOR_UPLOAD) { // don't switch back
                    const duplicates = await this.parseDuplicates(response.statusMessage);

                    this.updateFile(i, file => {
                        file.status = response.status;
                        file.statusMessage = response.statusMessage;
                        file.duplicates = duplicates;
                    });
                }

                if (response.status !== this.FILE_STATUS.DONE && response.status !== this.FILE_STATUS.ERROR) {
                    hasPendingFiles = true;
                }
            }
        }

        if (!hasPendingFiles) {
            this.setState({ status: this.PAGE_STATUS.DONE });
        } else {
            setTimeout(this.pollStatus, this.POLLING_PERIOD);
        }
    };

    parseDuplicates = async (statusMessage) => {
        if (statusMessage && statusMessage.includes("Duplicate")) {
            const PATTERN_GUID = new RegExp("[0-9a-f]{32}");
            const ids = statusMessage.match(PATTERN_GUID);
            if (ids) {
                let result = [];
                for (let i = 0; i < ids.length; i++) {
                    const id = ids[i];
                    let details = await requestApiJson("/res/" + id, {
                        method: "GET",
                        headers: {
                            "Content-Type": "application/json"
                        }
                    });
                    result.push({id, filePath: details.resource.filePath});
                }

                return result;
            }
        }

        return null;
    };

    onFileSelected = (e) => {
        if (e.target.files) {
            this.setState({
                files: [...this.state.files, ...e.target.files]
                    .filter((x, i, arr) => arr.findIndex(y => y.name === x.name) === i)
            });
        }
    };

    render() {
        return (<div>
            <MainHeader title="Upload files"/>
            <MainContent>
                <div className="upload-page pl-2 pr-2 pt-2 pb-2">
                    <div className="section">Path</div>
                    <div>{this.state.path}</div>
                    <div className="mt-3 section">Files</div>
                    <input style={{display:'none'}} ref={(input) => { this.fileInput = input; }} type="file" multiple="multiple" onChange={this.onFileSelected}/>
                    <div className="file-browser" onClick={e => this.fileInput.click()}>Add files</div>

                    <div className="mt-3">
                        {this.state.files && this.state.files.map(file => this.renderFileItem(file))}
                    </div>
                    {this.renderStats()}
                    {this.renderActionButton()}
                </div>
            </MainContent>
        </div>);
    }

    renderFileItem = (file) => {
        return (<>
            <div className="file-item" key={file.name}>
                {/* icon */}
                <img src="img/ic-file.png" className="file-icon" alt=""/>

                {/* file name & size */}
                <div className="file-info">
                    {file.status === this.FILE_STATUS.DONE
                        ? <a href={this.getResourceLink(file.id)} target="_blank" rel="noopener noreferrer" className="file-name">{file.name}</a>
                        : <div className="file-name">{file.name}</div>}
                    <div className="muted small">{formatFileSize(file.size)}</div>
                </div>

                {/* remove action OR upload status */}
                {this.renderStatus(file)}
            </div>
            {this.renderStatusMessage(file)}
        </>);
    };

    renderStatus = (file) => {
        if (this.state.status === this.PAGE_STATUS.NOT_STARTED) {
            return <div className="file-delete pl-3 pr-3" onClick={e => this.removeFile(file.name)}>❌</div>;
        }

        switch (file.status) {
            case this.FILE_STATUS.WAITING_FOR_UPLOAD: {
                return <div className="file-status">Waiting...</div>;
            }

            case this.FILE_STATUS.UPLOADING: {
                return <div className="file-status">Uploading...</div>;
            }

            case this.FILE_STATUS.PROCESSING: {
                return <div className="file-status">Indexing...</div>;
            }

            case this.FILE_STATUS.DONE: {
                return <div className="file-status">OK</div>;
            }

            case this.FILE_STATUS.ERROR: {
                 if (file.statusMessage.includes("Duplicate")) {
                    return <div className="file-status error">Duplicate</div>;
                } else {
                    return <div className="file-status error">{file.statusMessage}</div>;
                }
            }

            default: {
                return <div className="file-status error">{file.status}</div>;
            }
        }
    };

    renderStatusMessage = (file) => {
        if (file.duplicates && file.duplicates.length > 0) {
            return <>
                {file.duplicates.map(info => <a href={this.getResourceLink(info.id)} className="duplicate-info" target="_blank" rel="noopener noreferrer">
                    {info.filePath || info.id}
                </a>)}
            </>;
        } else {
            return null;
        }
    };

    removeFile = (fileName) => {
       this.setState({
           files: this.state.files.filter(f => f.name !== fileName)
       })
    };

    getTotal = (files) => {
        return {
            size: files.map(f => f.size).reduce((a, b) => a + b, 0),
            count:  files.length
        };
    };

    renderStats = () => {
        const statTotal = this.getTotal(this.state.files);
        const statUploaded = this.getTotal(this.state.files.filter(f => f.status === this.FILE_STATUS.PROCESSING || f.status === this.FILE_STATUS.DONE || f.status === this.FILE_STATUS.ERROR));
        const statIndexed = this.getTotal(this.state.files.filter(f => f.status === this.FILE_STATUS.DONE || f.status === this.FILE_STATUS.ERROR));
        const statErrors = this.getTotal(this.state.files.filter(f => f.status === this.FILE_STATUS.ERROR));

        return <div className="muted mt-3">
            <div className="flex">
                <div className="grow">Total</div>
                <div>{formatFileSize(statTotal.size)} ({statTotal.count} files)</div>
            </div>
            <div className="flex">
                <div className="grow">Uploaded</div>
                <div>{formatFileSize(statUploaded.size)} ({statUploaded.count} files)</div>
            </div>
            <div className="flex">
                <div className="grow">Indexed</div>
                <div>{formatFileSize(statIndexed.size)} ({statIndexed.count} files)</div>
            </div>
            <div className="flex">
                <div className="grow">Errors</div>
                <div className={statErrors.count > 0 ? "error" : ""}>{statErrors.count}</div>
            </div>
        </div>;
    };

    renderActionButton = () => {
        let button = {};

        switch (this.state.status) {
            case this.PAGE_STATUS.NOT_STARTED: {
                button.caption = "Upload";

                if (this.state.files && this.state.files.length > 0) {
                    button.enabled = true;
                    button.onClick = this.upload;
                } else {
                    button.enabled = false;
                    button.onClick = null;
                }
                break;
            }

            case this.PAGE_STATUS.UPLOADING: {
                button.caption = "Uploading...";
                button.enabled = false;
                button.onClick = null;
                break;
            }

            case this.PAGE_STATUS.INDEXING: {
                button.caption = "Indexing...";
                button.enabled = false;
                button.onClick = null;
                break;
            }

            case this.PAGE_STATUS.DONE: {
                button.caption = "Close";
                button.enabled = true;
                button.onClick = () => window.location = "/browse?path=" + encodeURIComponent(this.state.path);
                break;
            }

            default: {
                throw new Error("Invalid page status: " + this.state.status);
            }
        }

        return <button className="mt-3 p-2 wide-button" type="submit" disabled={!button.enabled} onClick={button.onClick}>{button.caption}</button>;
    };
}
